import { useCallback, useRef, useState } from 'react';

export interface ServerSyncedTextField {
  /** Current text — the user's while they edit, server-backed otherwise. */
  value: string;
  /** Call from the input's onChange with the new text. */
  setValue: (next: string) => void;
  /** Attach to the input's onFocus. */
  handleFocus: () => void;
  /** Attach to the input's onBlur (compose with any flush the caller needs). */
  handleBlur: () => void;
  /** Call once the caller's write for this field reached the server. */
  markCommitted: () => void;
  /** Attach to the input's ref so an unmount can stand in for a missing blur. */
  attachInput: (element: HTMLElement | null) => void;
}

/**
 * Local text state for an input whose value can also be written by someone else
 * (Bliksy, another tab) while the user has the field open.
 *
 * The field shows the server value unless one of two guards holds, and each
 * guard closes a way it could otherwise show the wrong text:
 *
 * - Focus. A server value that lands mid-typing must not yank the text out from
 *   under the caret. Losing that write is not acceptable either — the value does
 *   not change again, so nothing would bring it back before a page reload — so
 *   dropping focus re-evaluates against whatever the server holds by then. A
 *   focused input can also disappear without a blur event (the step card hides
 *   both fields when the step becomes a decision), hence the detach hand-off.
 * - Dirty. Text the user typed stands until the caller reports the write as
 *   committed. Without this an optimistic-update rollback from a failed PATCH
 *   would wipe the text off the screen while the retry buffer still holds it.
 *   A commit report is ignored while the field has focus, because the user is by
 *   then editing again and that edit outlives the write being acknowledged.
 */
export function useServerSyncedTextField(serverValue: string): ServerSyncedTextField {
  const [value, setValue] = useState(serverValue);
  const [appliedServerValue, setAppliedServerValue] = useState(serverValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Adjusted during render, not in an effect: the textarea is controlled, so an
  // effect would paint the stale text first and then correct it. A guarded write
  // stays pending — `appliedServerValue` only advances once the text is really
  // applied — and both guards are state, so dropping either re-renders and
  // applies whatever the server holds by then.
  if (serverValue !== appliedServerValue && !isFocused && !isDirty) {
    setAppliedServerValue(serverValue);
    setValue(serverValue);
  }

  // markCommitted runs from a settled promise, so it needs the focus state as of
  // then, not as of the render that built the handler.
  const isFocusedRef = useRef(false);

  const setValueFromUser = useCallback((next: string) => {
    setIsDirty(true);
    setValue(next);
  }, []);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    setIsFocused(false);
  }, []);

  const markCommitted = useCallback(() => {
    if (!isFocusedRef.current) setIsDirty(false);
  }, []);

  const attachInput = useCallback(
    (element: HTMLElement | null) => {
      if (!element && isFocusedRef.current) handleBlur();
    },
    [handleBlur]
  );

  return {
    value,
    setValue: setValueFromUser,
    handleFocus,
    handleBlur,
    markCommitted,
    attachInput
  };
}
